import { useState, useRef, useCallback, type TouchEvent } from 'react'
import FotoLightbox from './FotoLightbox'
import { type NotaItem } from './CapturaControls'
import { ItemAudio, ItemTexto } from './CapturaItems'

interface SessaoCapturaTimelineProps {
  items: NotaItem[]
  updateNota: (id: string | number, data: Record<string, unknown>) => void
  addLegendaVoz: (notaId: string | number, blob: Blob) => Promise<void>
  deleteNota: (nota: NotaItem) => void
  onOpenAnnotate?: (nota: NotaItem) => void
  onRefresh?: () => Promise<void>
}

// ── SwipeToDelete — revela botão de apagar ao deslizar ───────
function SwipeToDelete({ children, onDelete }: { children: React.ReactNode; onDelete: () => void }) {
  const [swipeX, setSwipeX] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const isSwipingRef = useRef(false)
  const THRESHOLD = 72

  const onTouchStart = (e: TouchEvent) => {
    startXRef.current = e.touches[0].clientX
    startYRef.current = e.touches[0].clientY
    isSwipingRef.current = false
  }

  const onTouchMove = (e: TouchEvent) => {
    const dx = e.touches[0].clientX - startXRef.current
    const dy = e.touches[0].clientY - startYRef.current
    if (!isSwipingRef.current && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
      isSwipingRef.current = true
    }
    if (!isSwipingRef.current) return
    if (dx < 0) {
      e.preventDefault()
      setSwipeX(Math.max(dx, -(THRESHOLD + 16)))
    }
  }

  const onTouchEnd = () => {
    if (swipeX <= -THRESHOLD) {
      setRevealed(true)
      setSwipeX(-THRESHOLD)
    } else {
      setRevealed(false)
      setSwipeX(0)
    }
    isSwipingRef.current = false
  }

  const handleDelete = () => {
    setSwipeX(0)
    setRevealed(false)
    onDelete()
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        className="absolute right-0 inset-y-0 flex items-center justify-center rounded-r-xl bg-red-500/20"
        style={{ width: THRESHOLD, opacity: revealed ? 1 : Math.abs(swipeX) / THRESHOLD }}
      >
        <button
          onClick={handleDelete}
          className="flex flex-col items-center gap-0.5 px-3 py-2 border-none bg-transparent cursor-pointer"
        >
          <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--color-vermelho)' }}>delete</span>
          <span className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--color-vermelho)' }}>Apagar</span>
        </button>
      </div>
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="rounded-xl p-3 bg-white/[0.04]"
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: !isSwipingRef.current && swipeX === 0 ? 'transform 0.2s ease' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ── VisualGridCell — thumbnail quadrado para fotos/croquis ──
function VisualGridCell({ nota, deleteNota, onOpenLightbox, onOpenAnnotate }: {
  nota: NotaItem
  deleteNota: (nota: NotaItem) => void
  onOpenLightbox: (nota: NotaItem) => void
  onOpenAnnotate?: (nota: NotaItem) => void
}) {
  const isUploading = nota._uploading
  const icon = nota.tipo === 'FOTO' ? 'image' : 'draw'
  const bgStyle = nota.tipo === 'DESENHO' && nota.signedUrl ? { background: 'white' } : {}

  return (
    <div
      className="relative rounded-xl overflow-hidden cursor-pointer active:opacity-80"
      style={{ aspectRatio: '1', ...bgStyle, background: bgStyle.background ?? 'rgba(255,255,255,0.04)' }}
      onClick={() => !isUploading && onOpenLightbox(nota)}
    >
      {nota.signedUrl ? (
        <img
          src={nota.signedUrl}
          alt={nota.tipo === 'FOTO' ? 'Foto' : 'Croqui'}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="material-symbols-outlined text-[#555] text-3xl">{icon}</span>
        </div>
      )}

      {/* Spinner de upload */}
      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <span className="material-symbols-outlined text-white animate-spin text-xl">progress_activity</span>
        </div>
      )}

      {/* Ações no canto (anotação + delete) */}
      {!isUploading && (
        <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
          {nota.tipo === 'FOTO' && onOpenAnnotate && nota.signedUrl && (
            <button
              onClick={e => { e.stopPropagation(); onOpenAnnotate(nota) }}
              className="w-7 h-7 rounded-full flex items-center justify-center border-none cursor-pointer bg-black/60"
              title="Anotar"
            >
              <span className="material-symbols-outlined text-[13px] text-white/80">draw</span>
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); deleteNota(nota) }}
            className="w-7 h-7 rounded-full flex items-center justify-center border-none cursor-pointer bg-black/60"
            title="Apagar"
          >
            <span className="material-symbols-outlined text-[13px] text-white/80">delete</span>
          </button>
        </div>
      )}
    </div>
  )
}

// ── Pull-to-refresh indicator ────────────────────────────────
function PullRefreshIndicator({ progress }: { progress: number }) {
  if (progress < 0.1) return null
  return (
    <div className="flex items-center justify-center gap-1.5 py-2">
      <span
        className="material-symbols-outlined text-[16px]"
        style={{ color: 'var(--color-hero)', transform: `rotate(${Math.min(progress, 1) * 180}deg)` }}
      >
        refresh
      </span>
      <span className="text-[11px]" style={{ color: 'var(--color-hero)' }}>
        {progress >= 1 ? 'Solte para atualizar' : 'Puxe para atualizar'}
      </span>
    </div>
  )
}

// ── SessaoCapturaTimeline ─────────────────────────────────────
export default function SessaoCapturaTimeline({ items, updateNota, addLegendaVoz, deleteNota, onOpenAnnotate, onRefresh }: SessaoCapturaTimelineProps) {
  const [lightbox, setLightbox] = useState<{ images: NotaItem[]; index: number } | null>(null)
  const [pullProgress, setPullProgress] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const pullStartRef = useRef(0)
  const PULL_THRESHOLD = 64

  const openLightbox = useCallback((nota: NotaItem) => {
    const visuais = items.filter(n => (n.tipo === 'FOTO' || n.tipo === 'DESENHO') && n.signedUrl)
    const index = visuais.findIndex(n => n.id === nota.id)
    if (index >= 0) setLightbox({ images: visuais, index })
  }, [items])

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (scrollRef.current?.scrollTop === 0) {
      pullStartRef.current = e.touches[0].clientY
    } else {
      pullStartRef.current = 0
    }
  }

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!pullStartRef.current || refreshing) return
    const dy = e.touches[0].clientY - pullStartRef.current
    if (dy > 0) setPullProgress(Math.min(dy / PULL_THRESHOLD, 1.3))
  }

  const handleTouchEnd = async () => {
    if (pullProgress >= 1 && onRefresh) {
      setRefreshing(true)
      setPullProgress(0)
      await onRefresh()
      setRefreshing(false)
    } else {
      setPullProgress(0)
    }
    pullStartRef.current = 0
  }

  if (!items.length && !refreshing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-6">
        <span className="material-symbols-outlined text-[#333] text-[40px]">photo_library</span>
        <p className="text-[12px] text-[#444]">Use os botões abaixo para capturar fotos,<br />áudios, textos e croquis.</p>
      </div>
    )
  }

  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <PullRefreshIndicator progress={refreshing ? 1 : pullProgress} />

        <div className="p-3 grid grid-cols-2 gap-2">
          {items.map(nota => {
            if (nota.tipo === 'FOTO' || nota.tipo === 'DESENHO') {
              return (
                <VisualGridCell
                  key={nota.id}
                  nota={nota}
                  deleteNota={deleteNota}
                  onOpenLightbox={openLightbox}
                  onOpenAnnotate={onOpenAnnotate}
                />
              )
            }
            return (
              <div key={nota.id} className="col-span-2">
                <SwipeToDelete onDelete={() => deleteNota(nota)}>
                  {nota.tipo === 'AUDIO'
                    ? <ItemAudio nota={nota} deleteNota={deleteNota} />
                    : <ItemTexto nota={nota} updateNota={updateNota} deleteNota={deleteNota} />
                  }
                </SwipeToDelete>
              </div>
            )
          })}
        </div>
      </div>

      {lightbox && (
        <FotoLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  )
}
